import React, {useContext, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import * as Opticks from 'opticks/lib/optimizely'
import Optimizely from '@optimizely/optimizely-sdk'

import {fetchOptimizelyDataFile} from '../../modules/actions'
import {getDataFile} from '../../modules/selectors'
import {trackExperimentServed} from '../../../analytics/modules/actions'

const OpticksContext = React.createContext(null)

export const useOpticks = () => useContext(OpticksContext)

interface OpticksProviderProps {
  children: React.ReactNode
  /** The url where your datafile is stored */
  dataFileUrl: string
  /** Fallback dataFile for local usage (e.g. testing) */
  fallbackDataFile?: Record<string, unknown>
}

enum ExperimentType {
  flag = 'feature',
  mvt = 'feature-test'
}

/**
 * Returns the experiments and decision for each experiment that Opticks decides
 */
export const experimentVariationsStore = (() => {
  let experiments: Record<string, string> = {}

  const add = (featureKey: string, variation: string) => {
    experiments[featureKey] = variation
  }

  /**
   *
   * @param asArray - if we should return the list of experiments as an array where each key has the format `experiment:variation`
   * @returns a list of experiments on Object or Array format
   */
  const getAll = (asArray = false) => {
    if (asArray) {
      return Object.keys(experiments).map(featureKey => {
        const variation = experiments[featureKey]
        return `${featureKey}:${variation}`
      })
    }

    return experiments
  }

  const clean = () => {
    experiments = {}
  }

  return {
    add,
    getAll,
    clean
  }
})()

const OpticksProvider = ({
  children,
  dataFileUrl,
  fallbackDataFile
}: OpticksProviderProps) => {
  const dataFile = useSelector(getDataFile) ?? fallbackDataFile
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchOptimizelyDataFile({dataFileUrl}))
  }, [dataFileUrl])

  if (dataFile) {
    Opticks.registerLibrary(Optimizely)
    // @ts-expect-error: Incorrectly typed in opticks package
    Opticks.initialize(dataFile, trackObject => {
      const {
        decisionInfo: {featureKey, featureEnabled, experimentKey, variationKey},
        type
      } = trackObject

      // trackObject can be one of two types: 'feature' (feature flag) and 'feature-test' (experiment)
      // 'feature' comes with featureKey and featureEnabled (true/false)
      // 'feature-test' comes with experimentKey and variationKey (a/b/c or any other values)
      if (type === ExperimentType.flag) {
        experimentVariationsStore.add(featureKey, featureEnabled ? 'b' : 'a')
      } else {
        experimentVariationsStore.add(experimentKey, variationKey)
      }

      // Making this call asynchronous to decouple it from the Redux update cycle,
      // preventing endless recursion when called from selectors.
      // The action will cause a store update -> selector -> toggle -> action -> store update -> etc.
      if (
        type === ExperimentType.flag &&
        // @ts-expect-error: Incorrectly typed in opticks package
        Opticks.isUserInRolloutAudience(featureKey)
      ) {
        setTimeout(
          () =>
            dispatch(
              trackExperimentServed(featureKey, featureEnabled ? 'b' : 'a')
            ),
          0
        )
      }

      // If the user does not qualify for the audience targeting, variationKey is null, skip tracking
      if (type === ExperimentType.mvt && variationKey !== null) {
        setTimeout(
          () => dispatch(trackExperimentServed(experimentKey, variationKey)),
          0
        )
      }
    })
  }

  return (
    <OpticksContext.Provider value={dataFile ? Opticks : undefined}>
      {children}
    </OpticksContext.Provider>
  )
}

export const getRawOpticks = () => Opticks

export default OpticksProvider
